import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import OutilService from "../../../services/smq/OutilService";
import ContexteOrganisationDialog from "./ContexteOrganisationDialog";
import { OUTIL_COLORS, paperSx, tableHeaderSx } from "./OutilContainer.styles";
import { ToolHint, ToolSectionHeader } from "./OutilShared";

const CATEGORY_OPTIONS = [
  { label: "Toutes", value: "" },
  { label: "Force", value: "force" },
  { label: "Faiblesse", value: "faiblesse" },
  { label: "Opportunité", value: "opportunite" },
  { label: "Menace", value: "menace" },
];

const TYPE_OPTIONS = [
  { label: "Tous", value: "" },
  { label: "Interne", value: "interne" },
  { label: "Externe", value: "externe" },
];

const EMPTY_DIALOG = { mode: "create", open: false, row: null };

const cellTextSx = {
  display: "-webkit-box",
  fontSize: 10.5,
  lineHeight: 1.35,
  overflow: "hidden",
  overflowWrap: "anywhere",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 3,
};

const labelFor = (options, value) => options.find((option) => option.value === value)?.label || "—";

const FilterChips = ({ counts, onChange, options, value }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
    {options.map((option) => (
      <Chip
        clickable
        key={option.label}
        label={`${option.label}  ${counts[option.value] || 0}`}
        onClick={() => onChange(option.value)}
        size="small"
        sx={{
          backgroundColor: value === option.value ? OUTIL_COLORS.navy : "#fff",
          border: `1px solid ${OUTIL_COLORS.border}`,
          color: value === option.value ? "#fff" : OUTIL_COLORS.navy,
          fontSize: 10,
          fontWeight: 700,
        }}
      />
    ))}
  </Box>
);

const ContexteOrganisation = ({ outilEntree, processus }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [category, setCategory] = useState("");
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(EMPTY_DIALOG);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [validatingId, setValidatingId] = useState(null);
  const [notification, setNotification] = useState({ message: "", open: false, severity: "success" });
  const requestIdRef = useRef(0);
  const entryReady = Boolean(
    outilEntree?.outil_entree_id &&
    String(outilEntree.processus_id) === String(processus?.processus_id)
  );

  const notify = useCallback((message, severity = "success") => {
    setNotification({ message, open: true, severity });
  }, []);

  const loadRows = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    if (!processus?.processus_id || !entryReady) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await OutilService.getContextes(processus.processus_id);
      if (!Array.isArray(response?.data)) {
        throw new Error("Une erreur s’est produite lors de la récupération des données.");
      }
      if (requestId === requestIdRef.current) setRows(response.data);
    } catch {
      if (requestId === requestIdRef.current) {
        setRows([]);
        setError("Une erreur s’est produite lors de la récupération des données.");
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [entryReady, processus?.processus_id]);

  useEffect(() => {
    setCategory("");
    setType("");
    setSearch("");
    setDialog(EMPTY_DIALOG);
    loadRows();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadRows]);

  const categoryCounts = useMemo(() => {
    const counts = { "": rows.length };
    rows.forEach((row) => { counts[row.categorie_swot] = (counts[row.categorie_swot] || 0) + 1; });
    return counts;
  }, [rows]);

  const typeCounts = useMemo(() => {
    const counts = { "": rows.length };
    rows.forEach((row) => { counts[row.type_contexte] = (counts[row.type_contexte] || 0) + 1; });
    return counts;
  }, [rows]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("fr");
    return rows.filter((row) => {
      if (category && row.categorie_swot !== category) return false;
      if (type && row.type_contexte !== type) return false;
      if (!query) return true;
      return [
        row.outil_code,
        row.processus_instance,
        row.enjeux,
        row.partie_interessee,
        row.risque,
        row.opportunite,
        row.action_requise,
        row.responsable,
        row.indicateur,
        row.observation,
        row.statut,
      ].some((item) => String(item || "").toLocaleLowerCase("fr").includes(query));
    });
  }, [category, rows, search, type]);

  const reset = () => {
    setCategory("");
    setType("");
    setSearch("");
  };

  const openDialog = (mode, row = null) => setDialog({ mode, open: true, row });

  const handleSaved = async (message) => {
    setDialog(EMPTY_DIALOG);
    notify(message);
    await loadRows();
  };

  const validateRow = async (row) => {
    if (row.statut === "Validé" || validatingId) return;
    setValidatingId(row.contexte_id);
    try {
      const response = await OutilService.validateContexte(row.contexte_id);
      notify(response?.message || "Contexte validé avec succès");
      await loadRows();
    } catch (validationError) {
      notify(validationError.message, "error");
    } finally {
      setValidatingId(null);
    }
  };

  const deleteRow = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      const response = await OutilService.deleteContexte(deleteTarget.contexte_id);
      setDeleteTarget(null);
      notify(response?.message || "Contexte supprimé avec succès");
      await loadRows();
    } catch (deleteError) {
      notify(deleteError.message, "error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Paper elevation={0} role="region" aria-label="Contexte de l'organisme" sx={{ ...paperSx, overflow: "hidden" }}>
      <ToolSectionHeader
        addDisabled={!entryReady}
        addTooltip={!entryReady ? "Enregistrez d’abord la fiche documentaire" : "Ajouter un contexte"}
        icon={<DomainOutlinedIcon sx={{ fontSize: 15, verticalAlign: "middle" }} />}
        onAdd={() => openDialog("create")}
        title="Contexte de l'organisme — Enjeux et SWOT"
      />
      <ToolHint />

      {error && (
        <Alert action={<Button color="inherit" onClick={loadRows}>Réessayer</Button>} severity="error" sx={{ mx: 1.5, my: 1 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ alignItems: { md: "center" }, backgroundColor: "#edf3f8", display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 1.2, px: 1.5, py: 1 }}>
        <Typography color={OUTIL_COLORS.textMuted} fontSize={9.5} fontWeight={700}>CATÉGORIE :</Typography>
        <FilterChips counts={categoryCounts} onChange={setCategory} options={CATEGORY_OPTIONS} value={category} />
        <Typography color={OUTIL_COLORS.textMuted} fontSize={9.5} fontWeight={700} sx={{ ml: { md: 1 } }}>TYPE :</Typography>
        <FilterChips counts={typeCounts} onChange={setType} options={TYPE_OPTIONS} value={type} />
        <TextField
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Filtrer toutes les colonnes..."
          size="small"
          sx={{ flex: 1, minWidth: { xs: "100%", md: 220 }, "& .MuiInputBase-root": { backgroundColor: "#fff", fontSize: 11, height: 34 } }}
          value={search}
        />
        <Typography color={OUTIL_COLORS.textMuted} fontSize={10} whiteSpace="nowrap">{filteredRows.length} entrée(s)</Typography>
        <Button disabled={!category && !type && !search} onClick={reset} size="small" startIcon={<RestartAltIcon />} sx={{ color: OUTIL_COLORS.navy, fontSize: 10, fontWeight: 700, textTransform: "none" }}>
          Tout réinit.
        </Button>
      </Box>

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table aria-label="Contextes de l'organisme" size="small" sx={{ minWidth: 1760, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              {[
                ["Code/Réf", 110], ["Processus cible", 130], ["Catégorie SWOT", 115], ["Enjeux", 180],
                ["Type", 90], ["PIP", 170], ["Risques", 160], ["Opportunités", 160],
                ["Action à mener", 170], ["Responsable", 130], ["Indicateur", 130],
                ["Observation", 150], ["Statut", 100], ["Actions", 135],
              ].map(([label, width]) => <TableCell key={label} sx={{ ...tableHeaderSx, width }}>{label}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell align="center" colSpan={14} sx={{ py: 3 }}><CircularProgress size={26} /></TableCell></TableRow>
            ) : filteredRows.length === 0 ? (
              <TableRow><TableCell align="center" colSpan={14} sx={{ color: "#8091a3", fontSize: 11, py: 2.5 }}>Aucune entrée — cliquez sur Ajouter.</TableCell></TableRow>
            ) : filteredRows.map((row) => (
              <TableRow
                hover
                key={row.contexte_id}
                onClick={() => openDialog("view", row)}
                sx={{ cursor: "pointer", "&:nth-of-type(even)": { backgroundColor: "#f8fafc" }, "& td": { borderColor: "#e3eaf1", verticalAlign: "top" } }}
              >
                <TableCell><Typography sx={{ ...cellTextSx, color: OUTIL_COLORS.navy, fontWeight: 800 }}>{row.outil_code || "—"}</Typography></TableCell>
                <TableCell><Typography sx={cellTextSx}>{row.processus_instance || "—"}</Typography></TableCell>
                <TableCell><Chip label={labelFor(CATEGORY_OPTIONS, row.categorie_swot)} size="small" sx={{ fontSize: 9.5, fontWeight: 700 }} /></TableCell>
                <TableCell><Typography sx={cellTextSx}>{row.enjeux || "—"}</Typography></TableCell>
                <TableCell><Typography sx={cellTextSx}>{labelFor(TYPE_OPTIONS, row.type_contexte)}</Typography></TableCell>
                <TableCell><Typography sx={cellTextSx}>{row.partie_interessee || "—"}</Typography></TableCell>
                <TableCell><Typography sx={cellTextSx}>{row.risque || "—"}</Typography></TableCell>
                <TableCell><Typography sx={cellTextSx}>{row.opportunite || "—"}</Typography></TableCell>
                <TableCell><Typography sx={cellTextSx}>{row.action_requise || "—"}</Typography></TableCell>
                <TableCell><Typography sx={cellTextSx}>{row.responsable || "—"}</Typography></TableCell>
                <TableCell><Typography sx={cellTextSx}>{row.indicateur || "—"}</Typography></TableCell>
                <TableCell><Typography sx={cellTextSx}>{row.observation || "—"}</Typography></TableCell>
                <TableCell>
                  <Chip
                    color={row.statut === "Validé" ? "success" : "warning"}
                    label={row.statut || "Brouillon"}
                    size="small"
                    sx={{ fontSize: 9.5, fontWeight: 800 }}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell onClick={(event) => event.stopPropagation()}>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title={row.statut === "Validé" ? "Déjà validé" : "Valider"}>
                      <span>
                        <IconButton
                          aria-label="Valider"
                          disabled={row.statut === "Validé" || validatingId === row.contexte_id}
                          onClick={() => validateRow(row)}
                          size="small"
                          sx={{ bgcolor: "#2e8b57", borderRadius: 1, color: "#fff", "&:hover": { bgcolor: "#237146" }, "&.Mui-disabled": { bgcolor: "#dce9e2", color: "#6f947e" } }}
                        >
                          {validatingId === row.contexte_id ? <CircularProgress color="inherit" size={16} /> : <CheckIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Modifier">
                      <IconButton aria-label="Modifier" onClick={() => openDialog("edit", row)} size="small" sx={{ bgcolor: OUTIL_COLORS.navy, borderRadius: 1, color: "#fff", "&:hover": { bgcolor: "#0d2c49" } }}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton aria-label="Supprimer" onClick={() => setDeleteTarget(row)} size="small" sx={{ bgcolor: "#ad1f24", borderRadius: 1, color: "#fff", "&:hover": { bgcolor: "#8f171c" } }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ContexteOrganisationDialog
        mode={dialog.mode}
        onClose={() => setDialog(EMPTY_DIALOG)}
        onSaved={handleSaved}
        open={dialog.open}
        outilEntree={outilEntree}
        processus={processus}
        row={dialog.row}
      />

      <Dialog onClose={() => !deleting && setDeleteTarget(null)} open={Boolean(deleteTarget)}>
        <DialogTitle>Supprimer le contexte</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Confirmez-vous la suppression de la fiche « {deleteTarget?.outil_code} » ? La pièce jointe restera conservée.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={deleting} onClick={() => setDeleteTarget(null)}>Annuler</Button>
          <Button color="error" disabled={deleting} onClick={deleteRow} variant="contained">
            {deleting ? <CircularProgress color="inherit" size={18} /> : "Supprimer"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        autoHideDuration={4500}
        onClose={() => setNotification((current) => ({ ...current, open: false }))}
        open={notification.open}
      >
        <Alert onClose={() => setNotification((current) => ({ ...current, open: false }))} severity={notification.severity} variant="filled">
          {notification.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ContexteOrganisation;
